import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3402';

export interface DemographicsSummary {
  totalPatrons: number;
  countries: Array<{ country: string; count: number; percentage: number }>;
  devices: Array<{ device: string; count: number; percentage: number }>;
  languages: Array<{ language: string; count: number; percentage: number }>;
  signupSources: Array<{ source: string; count: number; percentage: number }>;
  ageGroups?: Array<{ ageGroup: string; count: number; percentage: number }>;
  topCities: Array<{ city: string; country: string; count: number }>;
}

export interface ExportDemographicsData {
  format: 'csv' | 'json' | 'xlsx';
}

// New interfaces for enhanced demographics (Week 3)
export interface PageViewData {
  userId?: string;
  sessionId: string;
  path: string;
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  userAgent?: string;
  ipAddress?: string;
  loadTime?: number;
}

export interface GeographicData {
  countries: Array<{ country: string; count: number; percentage: number }>;
  regions: Array<{ region: string; country: string; count: number }>;
  cities: Array<{ city: string; region: string; country: string; count: number }>;
}

export interface DeviceData {
  devices: Array<{ device: string; count: number; percentage: number }>;
  browsers: Array<{ browser: string; count: number; percentage: number }>;
  operatingSystems: Array<{ os: string; count: number; percentage: number }>;
  mobileVsDesktop: { mobile: number; desktop: number; mobilePercentage: number };
}

export interface ReferralData {
  sources: Array<{ source: string; count: number; percentage: number; revenue: number }>;
  campaigns: Array<{ campaign: string; source: string; count: number; revenue: number }>;
  totalPatrons: number;
  totalRevenue: number;
}

export interface DemographicBreakdownData {
  ageRanges: Array<{ range: string; count: number; percentage: number; revenue: number }>;
  genders: Array<{ gender: string; count: number; percentage: number; revenue: number }>;
  languages: Array<{ language: string; count: number; percentage: number }>;
}

export interface DashboardData {
  geographic: GeographicData;
  devices: DeviceData;
  referrals: ReferralData;
  demographics: DemographicBreakdownData;
  summary: {
    totalPatrons: number;
    totalRevenue: number;
    topCountry: string;
    topDevice: string;
    topReferralSource: string;
  };
}

const demographicsService = {
  // Get demographics summary
  getSummary: async (): Promise<DemographicsSummary> => {
    const response = await axios.get(`${API_URL}/api/demographics/summary`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  // Export demographics data
  export: async (format: 'csv' | 'json' | 'xlsx'): Promise<Blob> => {
    const response = await axios.post(
      `${API_URL}/api/demographics/export`,
      { format },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        responseType: 'blob',
      }
    );
    return response.data;
  },

  // Get patron timeline (signup trends over time)
  getTimeline: async (params?: {
    startDate?: string;
    endDate?: string;
    groupBy?: 'day' | 'week' | 'month';
  }): Promise<Array<{ date: string; count: number }>> => {
    const response = await axios.get(`${API_URL}/api/demographics/timeline`, {
      params,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  // Week 3 Enhanced Demographics Methods
  
  /**
   * Track a page view with analytics data
   */
  trackPageView: async (data: PageViewData): Promise<void> => {
    try {
      await axios.post(`${API_URL}/api/demographics/track-pageview`, data);
    } catch (error) {
      console.error('Error tracking page view:', error);
      // Don't throw - tracking failures shouldn't break the app
    }
  },

  /**
   * Get comprehensive demographics dashboard
   */
  getDashboard: async (creatorId: string): Promise<DashboardData> => {
    const response = await axios.get(`${API_URL}/api/demographics/dashboard/${creatorId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data.data;
  },

  /**
   * Get geographic distribution
   */
  getGeographicDistribution: async (creatorId: string): Promise<GeographicData> => {
    const response = await axios.get(`${API_URL}/api/demographics/geographic/${creatorId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data.data;
  },

  /**
   * Get device distribution
   */
  getDeviceDistribution: async (creatorId: string): Promise<DeviceData> => {
    const response = await axios.get(`${API_URL}/api/demographics/devices/${creatorId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data.data;
  },

  /**
   * Get referral sources
   */
  getReferralSources: async (creatorId: string): Promise<ReferralData> => {
    const response = await axios.get(`${API_URL}/api/demographics/referrals/${creatorId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data.data;
  },

  /**
   * Get demographic breakdown
   */
  getDemographicBreakdown: async (creatorId: string): Promise<DemographicBreakdownData> => {
    const response = await axios.get(`${API_URL}/api/demographics/breakdown/${creatorId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data.data;
  },
};

// Helper functions for page tracking

/**
 * Generate a unique session ID for anonymous tracking
 */
export function getSessionId(): string {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
}

/**
 * Extract UTM parameters from URL
 */
export function extractUTMParams(): {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
} {
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get('utm_source') || undefined,
    utmMedium: params.get('utm_medium') || undefined,
    utmCampaign: params.get('utm_campaign') || undefined,
  };
}

/**
 * Track current page view (helper function)
 */
export function trackCurrentPage(userId?: string): void {
  const sessionId = getSessionId();
  const utm = extractUTMParams();
  
  demographicsService.trackPageView({
    userId,
    sessionId,
    path: window.location.pathname,
    referrer: document.referrer || undefined,
    ...utm,
  });
}

export default demographicsService;
